import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import {
  getAllSpecialEffects,
  getSpecialEffectById,
  DEFAULT_SPECIAL_EFFECT_ID,
  SPECIAL_EFFECTS_REGISTRY
} from '../specialEffects/SpecialEffectRegistry';

const SpecialEffectContext = createContext();

const isValidSpecialEffectId = (id) => {
  return !!SPECIAL_EFFECTS_REGISTRY[id];
};

export const SpecialEffectProvider = ({ children }) => {
  const [availableSpecialEffects, setAvailableSpecialEffects] = useState([]);
  const [selectedSpecialEffectId, setSelectedSpecialEffectId] = useState(() => {
    try {
      const savedEffectId = localStorage.getItem('selectedSpecialEffectId');
      return isValidSpecialEffectId(savedEffectId) ? savedEffectId : DEFAULT_SPECIAL_EFFECT_ID;
    } catch (error) {
      console.error("Error reading special effect ID from localStorage:", error);
      return DEFAULT_SPECIAL_EFFECT_ID;
    }
  });

  useEffect(() => {
    try {
      const effects = getAllSpecialEffects();
      setAvailableSpecialEffects(effects);
      if (!isValidSpecialEffectId(selectedSpecialEffectId)) {
        setSelectedSpecialEffectId(DEFAULT_SPECIAL_EFFECT_ID);
      }
    } catch (error) {
      console.error("Failed to load special effects:", error);
      setAvailableSpecialEffects([]);
    }
  }, []); // selectedSpecialEffectId removed as a dependency to avoid re-validation loops

  useEffect(() => {
    try {
      localStorage.setItem('selectedSpecialEffectId', selectedSpecialEffectId);
    } catch (error) {
      console.error("Error saving special effect ID to localStorage:", error);
    }
  }, [selectedSpecialEffectId]);

  const wrappedSetSelectedSpecialEffectId = useCallback((newId) => {
    if (isValidSpecialEffectId(newId)) {
      setSelectedSpecialEffectId(newId);
    } else {
      console.error(`Attempted to set invalid special effect ID: ${newId}`);
    }
  }, []);

  const activeSpecialEffect = useMemo(() => {
    return getSpecialEffectById(selectedSpecialEffectId);
  }, [selectedSpecialEffectId]);

  const value = useMemo(() => ({
    availableSpecialEffects,
    selectedSpecialEffectId,
    setSelectedSpecialEffectId: wrappedSetSelectedSpecialEffectId,
    activeSpecialEffect,
  }), [availableSpecialEffects, selectedSpecialEffectId, wrappedSetSelectedSpecialEffectId, activeSpecialEffect]);

  return (
    <SpecialEffectContext.Provider value={value}>
      {children}
    </SpecialEffectContext.Provider>
  );
};

export const useSpecialEffect = () => {
  const context = useContext(SpecialEffectContext);
  if (context === undefined) {
    throw new Error('useSpecialEffect must be used within a SpecialEffectProvider');
  }
  return context;
}; 